import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { profileAPI } from '../services/api.ts';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Task interfaces
interface Task {
  id: string | number;
  title: string;
  description?: string;
  points: number;
  status: 'pending' | 'completed';
  completedAt?: string;
  createdAt: string;
  dueDate?: string;
  taskDate?: string; // The date the task is assigned/created for
  category?: string;
  assignmentType?: 'team' | 'personal'; // Team goals vs Personal allowance goals
  // Handle both camelCase and PascalCase from API
  assignmentTypeId?: number; // 1 = Team, 2 = Personal (or vice versa)
  isTeamAssignment?: boolean;
  isPersonalAssignment?: boolean;
}

interface TaskSummary {
  pendingCount: number;
  completedCount: number;
  totalPoints: number;
  earnedPoints: number;
  // Team vs Personal breakdown
  teamPendingCount?: number;
  teamCompletedCount?: number;
  teamTotalPoints?: number;
  teamEarnedPoints?: number;
  personalPendingCount?: number;
  personalCompletedCount?: number;
  personalTotalPoints?: number;
  personalEarnedPoints?: number;
}

const Tasks: React.FC = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [summary, setSummary] = useState<TaskSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'pendingApproval' | 'completed'>('all');
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [serverError, setServerError] = useState(false); // Track server errors to prevent retries
  const [summaryServerError, setSummaryServerError] = useState(false); // Track summary server errors
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const hasInitialFetchRef = useRef(false); // Prevent double fetch in StrictMode
  const isInitialMountRef = useRef(true); // Track if this is the initial mount

  const pageSize = 10;

  // Fetch task summary
  const fetchSummary = async () => {
    // Don't retry if we already have a server error
    if (summaryServerError) return;
    
    setSummaryLoading(true);
    try {
      const data = await profileAPI.getTaskSummary();
      setSummary(data);
      setSummaryServerError(false); // Reset error flag on success
    } catch (err: any) {
      console.error('Error fetching task summary:', err);
      // Check if it's a server error (404, 500, etc.)
      const status = err.response?.status || err.status;
      if (status === 404 || status === 500 || status >= 400) {
        setSummaryServerError(true);
        setSummary(null);
      }
    } finally {
      setSummaryLoading(false);
    }
  };

  // Fetch tasks with pagination
  const fetchTasks = async (page: number, isLoadMore: boolean = false, filter?: 'all' | 'pending' | 'pendingApproval' | 'completed') => {
    // Use provided filter or current statusFilter
    const currentFilter = filter !== undefined ? filter : statusFilter;
    
    // Don't retry if we already have a server error (unless it's a filter change)
    if (serverError && filter === undefined) return;
    if (loading && !isLoadMore) return;
    if (!isLoadMore && !hasMore && filter === undefined) return;

    setLoading(true);
    setError(null);

    try {
      // Map status filter to StatusId:
      // 0 = pending, 1 = Pending Approval, 2 = Completed
      let statusId: number | undefined;
      if (currentFilter === 'pending') {
        statusId = 0;
      } else if (currentFilter === 'pendingApproval') {
        statusId = 1;
      } else if (currentFilter === 'completed') {
        statusId = 2;
      }
      // 'all' -> undefined (no filter)

      console.log('[Tasks] Fetching tasks with:', { PageNumber: page, PageSize: pageSize, StatusId: statusId, filter: currentFilter });

      const response = await profileAPI.getTasks({
        PageNumber: page,
        PageSize: pageSize,
        StatusId: statusId
      });

      // Normalize task data - handle both camelCase and PascalCase
      const normalizedTasks: Task[] = (response.data || []).map((item: any) => {
        // Map StatusId to status string: 0 = pending, 1 = pending approval, 2 = completed
        let statusStr = 'pending';
        if (item.statusId !== undefined || item.StatusId !== undefined) {
          const statusId = item.statusId ?? item.StatusId;
          if (statusId === 2) {
            statusStr = 'completed';
          } else if (statusId === 1) {
            statusStr = 'pending'; // Keep as 'pending' for display, but filtered by StatusId = 1
          } else if (statusId === 0) {
            statusStr = 'pending';
          }
        } else if (item.status || item.Status) {
          statusStr = (item.status || item.Status).toLowerCase();
        }
        
        return {
          ...item,
          // Normalize fields 
          status: statusStr,
          // Normalize taskDate - handle both camelCase and PascalCase
          taskDate: item.taskDate || item.TaskDate || item.createdAt || item.CreatedAt,
        };
      });
      
      const hasMoreData = response.hasMore !== false && normalizedTasks.length === pageSize;

      if (isLoadMore) {
        setTasks(prev => [...prev, ...normalizedTasks]);
      } else {
        // Sort: pending first, then by due date
        const sorted = [...normalizedTasks].sort((a, b) => {
          if (a.status !== b.status) {
            return a.status === 'pending' ? -1 : 1;
          }
          if (a.dueDate && b.dueDate) {
            return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
          }
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });
        setTasks(sorted);
      }

      setHasMore(hasMoreData);
      setPageNumber(page);
      setServerError(false); // Reset error flag on success
    } catch (err: any) {
      console.error('Error fetching tasks:', err);
      // Check if it's a server error (404, 500, etc.)
      const status = err.response?.status || err.status;
      if (status === 404 || status === 500 || status >= 400) {
        setServerError(true);
        setHasMore(false); // Stop infinite scroll
        setError('Tasks feature is not available. Please contact support if this issue persists.');
        toast.error('Tasks feature is not available');
      } else {
        setError(err.message || 'Failed to load tasks');
        toast.error('Failed to load tasks');
      }
    } finally {
      setLoading(false);
    }
  };

  // Mark task as complete
  const handleMarkComplete = async (taskId: string | number) => {
    try {
      await profileAPI.markTaskComplete(taskId);
      
      // Update local state
      setTasks(prev => prev.map(task => 
        task.id === taskId 
          ? { ...task, status: 'completed' as const, completedAt: new Date().toISOString() }
          : task
      ));

      // Refresh summary
      fetchSummary();

      toast.success('Task marked as complete!');
    } catch (err: any) {
      console.error('Error marking task as complete:', err);
      toast.error(err.message || 'Failed to mark task as complete');
    }
  };

  // Initial load
  useEffect(() => {
    // Prevent double fetch in StrictMode
    if (!hasInitialFetchRef.current) {
      hasInitialFetchRef.current = true;
      fetchSummary();
      fetchTasks(1, false);
    }
  }, []);

  // Refetch when filter changes
  useEffect(() => {
    // Skip on initial mount (handled by initial load useEffect)
    // Check if this is the first time this effect runs
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false; // Mark that initial mount is done
      return; // Skip the fetch on initial mount
    }
    
    // Don't refetch if we have a server error
    if (serverError) return;
    
    // Clear existing tasks and reset state when filter changes
    setTasks([]);
    setPageNumber(1);
    setHasMore(true);
    setServerError(false); // Reset error when filter changes (user might want to retry)
    setError(null); // Clear any previous errors
    fetchTasks(1, false, statusFilter); // Pass the current statusFilter explicitly
  }, [statusFilter]);

  // Infinite scroll observer
  useEffect(() => {
    if (!loadMoreRef.current) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        // Don't trigger if there's a server error, loading, or if tasks are empty (filter change in progress)
        if (entries[0].isIntersecting && hasMore && !loading && !serverError && tasks.length > 0) {
          fetchTasks(pageNumber + 1, true);
        }
      },
      { threshold: 0.1 }
    );

    observerRef.current.observe(loadMoreRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loading, pageNumber, statusFilter, serverError, tasks.length]);

  // Format date helper
  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Format relative date helper
  const formatRelativeDate = (dateString?: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'Overdue';
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    if (diffDays < 7) return `Due in ${diffDays} days`;
    return formatDate(dateString);
  };

  return (
    <div className="container-app py-6">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Tasks</h1>
      </div>

      {/* Summary Cards */}
      {!summaryLoading && summary && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl shadow-md p-4 border border-orange-200">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <div className="text-sm font-medium text-orange-700">Pending Tasks</div>
            </div>
            <div className="text-3xl font-bold text-orange-600">{summary.pendingCount || 0}</div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-md p-4 border border-green-200">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm font-medium text-green-700">Personal Points</div>
            </div>
            <div className="text-3xl font-bold text-green-600">
              {summary.earnedPoints || 0} <span className="text-lg text-green-500">/ {summary.totalPoints || 0}</span>
            </div>
          </div>
        </div>
      )}

      {/* Status Filter Tabs */}
      <div className="flex gap-2 mb-4 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setStatusFilter('all')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            statusFilter === 'all'
              ? 'bg-white text-primary shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          All
        </button>
        <button
          onClick={() => setStatusFilter('pending')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            statusFilter === 'pending'
              ? 'bg-white text-primary shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Pending
        </button>
        <button
          onClick={() => setStatusFilter('pendingApproval')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            statusFilter === 'pendingApproval'
              ? 'bg-white text-primary shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Pending Approval
        </button>
        <button
          onClick={() => setStatusFilter('completed')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            statusFilter === 'completed'
              ? 'bg-white text-primary shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Completed
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-red-600">{error}</p>
          {serverError && (
            <button
              onClick={() => {
                setServerError(false);
                setError(null);
                setHasMore(true);
                fetchTasks(1, false);
              }}
              className="mt-2 text-sm text-red-700 underline hover:text-red-900"
            >
              Try again
            </button>
          )}
        </div>
      )}

      {/* Tasks List */}
      <div className="space-y-3">
        {tasks.length === 0 && !loading && !error && (
          <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
            No tasks found
          </div>
        )}

        {tasks.map((task) => (
          <div
            key={task.id}
            className={`bg-white shadow-md rounded-lg p-4 border-l-4 border-l-primary transition-all hover:shadow-lg ${
              task.status === 'completed' ? 'opacity-75' : ''
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h3 className={`font-semibold text-base ${task.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                    {task.title}
                  </h3>
                  {task.category && (
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                      {task.category}
                    </span>
                  )}
                </div>

                {task.description && (
                  <p className="text-sm text-gray-600 mb-3 mt-1">{task.description}</p>
                )}

                <div className="flex items-center gap-4 text-xs flex-wrap">
                  {task.taskDate && (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 font-medium">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>Task Date: {formatDate(task.taskDate)}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full font-medium bg-gray-50 text-gray-700">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{task.points} points</span>
                  </div>
                  {task.dueDate && task.status === 'pending' && (
                    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${
                      new Date(task.dueDate) < new Date() 
                        ? 'bg-red-50 text-red-700 font-medium' 
                        : 'bg-gray-50 text-gray-600'
                    }`}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>{formatRelativeDate(task.dueDate)}</span>
                    </div>
                  )}
                  {task.status === 'completed' && task.completedAt && (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 text-green-700 font-medium">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Completed {formatDate(task.completedAt)}</span>
                    </div>
                  )}
                </div>
              </div>

              {task.status === 'pending' && (
                <button
                  onClick={() => handleMarkComplete(task.id)}
                  className="ml-4 flex-shrink-0 px-5 py-2.5 text-sm font-semibold rounded-lg transition-all shadow-md hover:shadow-lg bg-primary text-white hover:bg-primary-dark"
                >
                  Complete
                </button>
              )}

              {task.status === 'completed' && (
                <div className="ml-4 flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center shadow-md">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Loading indicator */}
        {loading && tasks.length === 0 && (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <div className="inline-block animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"></div>
            <p className="text-sm text-gray-500 mt-2">Loading tasks...</p>
          </div>
        )}

        {/* Load more trigger */}
        <div ref={loadMoreRef} className="h-4">
          {loading && tasks.length > 0 && (
            <div className="text-center py-4">
              <div className="inline-block animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full"></div>
              <p className="text-xs text-gray-500 mt-2">Loading more...</p>
            </div>
          )}
          {!hasMore && tasks.length > 0 && (
            <div className="text-center py-4 text-sm text-gray-500">
              No more tasks to load
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Tasks;

